import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AddBlocksForm, TemplateForm } from './AddBlocksForm';
import { useCreateBlocks, useAddTemplatesToBlocks } from '../../hooks/api/use-blocks.hook';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import FileDragAndDropField from '../FileDragAndDropField';
import FileDisplay from '../FileDisplay';
import { BLOCKS_CREATE_COUNT_LIMIT, BLOCKS_CREATE_SIZE_LIMIT } from '../../../../types/constants/block';

export const AddBlocks = () => {
  const navigate = useNavigate();
  const [createBlocks, { error: createError }] = useCreateBlocks();
  const [addTemplatesToBlocks, { error: addTemplatesError }] = useAddTemplatesToBlocks();
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<AddBlocksForm>({
    resolver: classValidatorResolver(AddBlocksForm, {
      transformer: { targetMaps: [{ target: AddBlocksForm, properties: { files: Array } }] },
    }),
    values: undefined,
  });

  const filesValue = watch('files');

  const onSubmit: SubmitHandler<AddBlocksForm> = async (values) => {
    const formData = values.files.reduce((acc: FormData, file) => {
      acc.append('files', file);
      return acc;
    }, new FormData());

    formData.append(
      'blocks',
      JSON.stringify(
        values.files.map((file) => ({
          name: file.name.replace('.dwg', ''),
        })),
      ),
    );

    const blocks = await createBlocks(formData);

    await addTemplatesToBlocks({
      blockIds: blocks.map(({ id }) => id),
      templateIds: values.templates.map(({ id }) => id),
    });

    navigate('..');
  };

  const renderContent = () => (
    <div>
      <Controller
        control={control}
        name="templates"
        render={({ field }) => (
          <Autocomplete
            {...register('templates')}
            loading={loadingTemplates}
            sx={{ mt: '1rem' }}
            multiple
            onChange={(_event, selectedOptions) => field.onChange([...selectedOptions])}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(template) => (template as TemplateForm)?.name ?? ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            options={templatesResponse?.pages.flatMap(({ result }) => result) ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Template(s)"
                error={Boolean(errors.templates)}
                helperText={errors.templates?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="files"
        render={() => (
          <FileDragAndDropField
            multiple
            error={errors.files?.message}
            accept={{
              'image/x-dwg': ['.dwg'],
              'application/acad': ['.dwg'],
              'image/vnd.dwg': ['.dwg'],
              'application/x-dwg': ['.dwg'],
            }}
            fileSizeLimit={BLOCKS_CREATE_SIZE_LIMIT}
            onDropRejected={(rejectedFiles) => {
              const errorMessage =
                rejectedFiles[0].errors[0].code === 'file-too-large'
                  ? `File size exceeds ${BLOCKS_CREATE_SIZE_LIMIT / 1024 / 1024}MB`
                  : 'Invalid file type. Only .dwg files are allowed.';

              setError('files', {
                type: 'custom',
                message: errorMessage,
              });
            }}
            onDrop={(files: (File & { handle?: FileSystemHandle })[]) => {
              if ((filesValue ?? []).length === BLOCKS_CREATE_COUNT_LIMIT) {
                setError('files', {
                  type: 'custom',
                  message: `You can only upload ${BLOCKS_CREATE_COUNT_LIMIT} files.`,
                });
                return;
              }

              // Fix class-transformer issue with FileSystemHandle
              files.forEach((file) => {
                const newFile = file;

                delete newFile?.handle;
              });

              const limit = BLOCKS_CREATE_COUNT_LIMIT - (filesValue ?? []).length;

              setValue('files', [...files.slice(0, limit), ...(filesValue ?? [])]);

              clearErrors('files');
            }}
          />
        )}
      />
      {(filesValue ?? []).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <FileDisplay
            maxFiles={BLOCKS_CREATE_COUNT_LIMIT}
            files={Array.from(filesValue ?? []).sort((a, b) => a.name.localeCompare(b.name))}
            onRemove={(file: File) => {
              const updatedFiles = Array.from(filesValue ?? []).filter((f) => f.name !== file.name);
              setValue('files', updatedFiles);
            }}
          />
        </Box>
      )}
    </div>
  );

  return (
    <Dialog open={true} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Add Blocks</DialogTitle>
        <DialogContent>
          {renderContent()}
          {createError || addTemplatesError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {createError?.response?.data?.message ?? addTemplatesError?.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Add Blocks
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
